import React, { useState, useEffect } from 'react'
import { 
  Edit, Trash2, Lock, UserPlus, Search, Filter, 
  ChevronDown, Eye, DollarSign, Shield, Mail,
  Phone, Calendar, MapPin, RefreshCw, LogIn,
  CheckCircle, XCircle, Clock, AlertCircle
} from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../services/supabase'
import { formatCurrency, formatDate } from '../../utils/formatters'
import toast from 'react-hot-toast'

const UserManagement = () => {
  const { profile } = useAuth()
  const [users, setUsers] = useState([])
  const [filteredUsers, setFilteredUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [kycFilter, setKycFilter] = useState('all')
  const [selectedUser, setSelectedUser] = useState(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  
  const [editData, setEditData] = useState({
    full_name: '',
    email: '',
    phone: '',
    balance: 0,
    status: 'active',
    role: 'user'
  })

  const [newUserData, setNewUserData] = useState({
    email: '',
    password: '',
    username: '',
    full_name: '',
    phone: '',
    balance: 0,
    role: 'user'
  })

  useEffect(() => {
    loadUsers()
  }, [])

  useEffect(() => {
    filterUsers()
  }, [users, searchTerm, statusFilter, kycFilter])

  const loadUsers = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      setUsers(data || [])
    } catch (error) {
      console.error('Error loading users:', error)
      toast.error('Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  const filterUsers = () => {
    let filtered = [...users]

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(user => 
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => user.status === statusFilter)
    }

    // KYC filter
    if (kycFilter !== 'all') {
      filtered = filtered.filter(user => user.kyc_status === kycFilter)
    }

    setFilteredUsers(filtered)
  }

  const handleEditUser = (user) => {
    setSelectedUser(user)
    setEditData({
      full_name: user.full_name || user.name || '',
      email: user.email,
      phone: user.phone || '',
      balance: user.balance || 0,
      status: user.status || 'active',
      role: user.role || 'user'
    })
    setShowEditModal(true)
  }

  const handleViewDetails = (user) => {
    setSelectedUser(user)
    setShowDetailsModal(true)
  }

  const saveUserEdit = async () => {
    if (!selectedUser) return

    try {
      const { error } = await supabase
        .from('users')
        .update({
          full_name: editData.full_name,
          email: editData.email,
          phone: editData.phone,
          balance: parseFloat(editData.balance),
          status: editData.status,
          role: editData.role
        })
        .eq('id', selectedUser.id)

      if (error) throw error

      // Log activity
      await supabase.from('activity_logs').insert({
        user_id: profile.id,
        action: 'user_updated',
        details: `Updated user: ${selectedUser.email}`,
        metadata: { updated_fields: editData }
      })

      toast.success('User updated successfully!')
      setShowEditModal(false)
      loadUsers()
    } catch (error) {
      console.error('Error updating user:', error)
      toast.error('Failed to update user')
    }
  }

  const handleDeleteUser = async (user) => {
    if (!confirm(`Delete user ${user.email}? This action cannot be undone.`)) return

    try {
      const { error } = await supabase
        .from('users')
        .delete()
        .eq('id', user.id)

      if (error) throw error

      // Log activity
      await supabase.from('activity_logs').insert({
        user_id: profile.id,
        action: 'user_deleted',
        details: `Deleted user: ${user.email}`
      })

      toast.success('User deleted successfully!')
      loadUsers()
    } catch (error) {
      console.error('Error deleting user:', error)
      toast.error('Failed to delete user')
    }
  }

  const handleLoginAsUser = async (user) => {
    if (!confirm(`Login as ${user.email}? This will log you out of your admin account.`)) return

    try {
      // Log activity before switching
      await supabase.from('activity_logs').insert({
        user_id: profile.id,
        action: 'login_as_user',
        details: `Admin logged in as user: ${user.email}`
      })

      // Store admin session info
      sessionStorage.setItem('admin_impersonation', JSON.stringify({
        admin_id: profile.id,
        target_user_id: user.id,
        timestamp: new Date().toISOString()
      }))

      // Sign in as the target user (would need proper backend implementation)
      toast.success(`Switching to ${user.email}'s account...`)
      
      // In production, you'd implement proper impersonation logic
      window.location.href = '/dashboard'
    } catch (error) {
      console.error('Error logging in as user:', error)
      toast.error('Failed to switch accounts')
    }
  }

  const handleCreateUser = async () => {
    if (!newUserData.email || !newUserData.password || !newUserData.username) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      // Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: newUserData.email,
        password: newUserData.password,
        options: {
          data: {
            username: newUserData.username,
            full_name: newUserData.full_name
          }
        }
      })

      if (authError) throw authError

      // Update user profile
      const { error: updateError } = await supabase
        .from('users')
        .update({
          username: newUserData.username,
          full_name: newUserData.full_name,
          phone: newUserData.phone,
          balance: parseFloat(newUserData.balance),
          role: newUserData.role,
          status: 'active'
        })
        .eq('id', authData.user.id)

      if (updateError) throw updateError

      // Log activity
      await supabase.from('activity_logs').insert({
        user_id: profile.id,
        action: 'user_created',
        details: `Created new user: ${newUserData.email}`
      })

      toast.success('User created successfully!')
      setShowCreateModal(false)
      setNewUserData({
        email: '',
        password: '',
        username: '',
        full_name: '',
        phone: '',
        balance: 0,
        role: 'user'
      })
      loadUsers()
    } catch (error) {
      console.error('Error creating user:', error)
      toast.error(error.message || 'Failed to create user')
    }
  }

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      inactive: 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400',
      suspended: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
    }
    return badges[status] || badges.active
  }

  const getKycBadge = (status) => {
    const badges = {
      verified: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      pending: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
      processing: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
      rejected: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
    }
    return badges[status] || badges.pending
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-3 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
            User Management
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
            {filteredUsers.length} of {users.length} users
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <button
            onClick={loadUsers}
            className="px-4 py-2 border-2 border-gray-300 dark:border-dark-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center justify-center gap-2 transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
            Refresh
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 transition-colors"
          >
            <UserPlus className="w-5 h-5" />
            Create User
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-dark-800 rounded-lg shadow-lg p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>

          {/* KYC Filter */}
          <select
            value={kycFilter}
            onChange={(e) => setKycFilter(e.target.value)}
            className="px-4 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
          >
            <option value="all">All KYC Status</option>
            <option value="verified">Verified</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-dark-800 rounded-lg shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50 dark:bg-dark-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden sm:table-cell">
                  Balance
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">
                  KYC
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden xl:table-cell">
                  Joined
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-dark-700">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    No users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map(user => (
                  <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-blue-600 dark:text-blue-400 font-semibold text-sm">
                            {(user.full_name || user.name || user.username || user.email)?.[0]?.toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm text-gray-900 dark:text-white truncate">
                            {user.full_name || user.name || user.username}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {user.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 hidden sm:table-cell">
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {formatCurrency(user.balance || 0)}
                      </span>
                    </td>
                    <td className="px-4 py-4 hidden md:table-cell">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(user.status)}`}>
                        {user.status || 'active'}
                      </span>
                    </td>
                    <td className="px-4 py-4 hidden lg:table-cell">
                      <span className={`px-2 py-1 text-xs rounded-full ${getKycBadge(user.kyc_status)}`}>
                        {user.kyc_status || 'pending'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400 hidden xl:table-cell">
                      {formatDate(user.created_at)}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewDetails(user)}
                          className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditUser(user)}
                          className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                          title="Edit User"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleLoginAsUser(user)}
                          className="p-2 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors hidden sm:block"
                          title="Login As User"
                        >
                          <LogIn className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user)}
                          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Delete User"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-800 rounded-xl max-w-2xl w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-4">
              Edit User: {selectedUser.email}
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  value={editData.full_name}
                  onChange={(e) => setEditData({...editData, full_name: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={editData.email}
                  onChange={(e) => setEditData({...editData, email: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  value={editData.phone}
                  onChange={(e) => setEditData({...editData, phone: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Balance
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={editData.balance}
                  onChange={(e) => setEditData({...editData, balance: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Status
                </label>
                <select
                  value={editData.status}
                  onChange={(e) => setEditData({...editData, status: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Role
                </label>
                <select
                  value={editData.role}
                  onChange={(e) => setEditData({...editData, role: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 px-4 py-2 border-2 border-gray-300 dark:border-dark-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveUserEdit}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-800 rounded-xl max-w-2xl w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-4">
              Create New User
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={newUserData.email}
                  onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                  placeholder="user@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Username *
                </label>
                <input
                  type="text"
                  value={newUserData.username}
                  onChange={(e) => setNewUserData({...newUserData, username: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                  placeholder="johndoe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Password *
                </label>
                <input
                  type="password"
                  value={newUserData.password}
                  onChange={(e) => setNewUserData({...newUserData, password: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                  placeholder="Min. 8 characters"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  value={newUserData.full_name}
                  onChange={(e) => setNewUserData({...newUserData, full_name: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  value={newUserData.phone}
                  onChange={(e) => setNewUserData({...newUserData, phone: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                  placeholder="+1 (555) 000-0000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Initial Balance
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={newUserData.balance}
                  onChange={(e) => setNewUserData({...newUserData, balance: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Role
                </label>
                <select
                  value={newUserData.role}
                  onChange={(e) => setNewUserData({...newUserData, role: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  setNewUserData({
                    email: '',
                    password: '',
                    username: '',
                    full_name: '',
                    phone: '',
                    balance: 0,
                    role: 'user'
                  })
                }}
                className="flex-1 px-4 py-2 border-2 border-gray-300 dark:border-dark-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUser}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {showDetailsModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-800 rounded-xl max-w-3xl w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                User Details
              </h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Profile Section */}
              <div className="flex items-center gap-4 pb-6 border-b border-gray-200 dark:border-dark-700">
                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 dark:text-blue-400 font-bold text-2xl">
                    {(selectedUser.full_name || selectedUser.name || selectedUser.username || selectedUser.email)?.[0]?.toUpperCase()}
                  </span>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 dark:text-white">
                    {selectedUser.full_name || selectedUser.name || selectedUser.username}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedUser.email}
                  </p>
                  <div className="flex gap-2 mt-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(selectedUser.status)}`}>
                      {selectedUser.status || 'active'}
                    </span>
                    <span className={`px-2 py-1 text-xs rounded-full ${getKycBadge(selectedUser.kyc_status)}`}>
                      KYC: {selectedUser.kyc_status || 'pending'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Account Information */}
              <div>
                <h5 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Account Information
                </h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Balance</label>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {formatCurrency(selectedUser.balance || 0)}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Account Type</label>
                    <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                      {selectedUser.account_type || 'personal'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Role</label>
                    <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                      {selectedUser.role || 'user'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Member Since</label>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatDate(selectedUser.created_at)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h5 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  Contact Information
                </h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Email</label>
                    <p className="text-sm font-medium text-gray-900 dark:text-white break-all">
                      {selectedUser.email}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Phone</label>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {selectedUser.phone || 'Not provided'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Username</label>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      @{selectedUser.username || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Date of Birth</label>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {selectedUser.date_of_birth ? formatDate(selectedUser.date_of_birth) : 'Not provided'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Address Information */}
              {(selectedUser.street_address || selectedUser.address) && (
                <div>
                  <h5 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    Address
                  </h5>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {selectedUser.street_address || selectedUser.address}
                    {selectedUser.city && selectedUser.state && (
                      <>
                        <br />
                        {selectedUser.city}, {selectedUser.state} {selectedUser.zip_code}
                      </>
                    )}
                  </p>
                </div>
              )}

              {/* KYC Information */}
              {selectedUser.kyc_status !== 'pending' && (
                <div>
                  <h5 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    KYC Information
                  </h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">KYC Status</label>
                      <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                        {selectedUser.kyc_status}
                      </p>
                    </div>
                    {selectedUser.kyc_submitted_at && (
                      <div>
                        <label className="text-xs text-gray-500 dark:text-gray-400">Submitted At</label>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatDate(selectedUser.kyc_submitted_at)}
                        </p>
                      </div>
                    )}
                    {selectedUser.kyc_verified_at && (
                      <div>
                        <label className="text-xs text-gray-500 dark:text-gray-400">Verified At</label>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatDate(selectedUser.kyc_verified_at)}
                        </p>
                      </div>
                    )}
                    {selectedUser.kyc_rejection_reason && (
                      <div className="sm:col-span-2">
                        <label className="text-xs text-gray-500 dark:text-gray-400">Rejection Reason</label>
                        <p className="text-sm font-medium text-red-600 dark:text-red-400">
                          {selectedUser.kyc_rejection_reason}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Employment Information */}
              {selectedUser.employment_status && (
                <div>
                  <h5 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <Briefcase className="w-5 h-5" />
                    Employment
                  </h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">Status</label>
                      <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                        {selectedUser.employment_status.replace(/_/g, ' ')}
                      </p>
                    </div>
                    {selectedUser.employer_name && (
                      <div>
                        <label className="text-xs text-gray-500 dark:text-gray-400">Employer</label>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {selectedUser.employer_name}
                        </p>
                      </div>
                    )}
                    {selectedUser.job_title && (
                      <div>
                        <label className="text-xs text-gray-500 dark:text-gray-400">Job Title</label>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {selectedUser.job_title}
                        </p>
                      </div>
                    )}
                    {selectedUser.annual_income && (
                      <div>
                        <label className="text-xs text-gray-500 dark:text-gray-400">Annual Income</label>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {(selectedUser.annual_income || '').replace(/_/g, ' - ')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Last Login */}
              {selectedUser.last_login && (
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Last Login</label>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {formatDate(selectedUser.last_login)}
                  </p>
                </div>
              )}
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-dark-700 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => {
                  setShowDetailsModal(false)
                  handleEditUser(selectedUser)
                }}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 transition-colors"
              >
                <Edit className="w-5 h-5" />
                Edit User
              </button>
              <button
                onClick={() => {
                  setShowDetailsModal(false)
                  handleLoginAsUser(selectedUser)
                }}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2 transition-colors"
              >
                <LogIn className="w-5 h-5" />
                Login As User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserManagement